import { Select, type SelectProps } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ALL_CURRENCIES,
  SUGGESTED_CURRENCIES,
  type Currency,
} from '@/hooks/useCurrency';
import { searchShowSearch } from '@/utils/selectHelpers';

type SearchableCurrency = Currency & { searchTokens: string };

const SEARCHABLE_CURRENCIES: SearchableCurrency[] = ALL_CURRENCIES.map(
  (currency) => ({
    ...currency,
    searchTokens:
      `${currency.code} ${currency.description} ${currency.symbol || ''}`.toLowerCase(),
  }),
);

const OTHER_CURRENCIES = ALL_CURRENCIES.filter(({ suggested }) => !suggested);

const toOption = ({ code, symbol }: Currency) => ({
  value: code,
  label: `${code}${symbol ? ` (${symbol})` : ''}`,
});

const CurrencySelect = (props: SelectProps) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');

  const onSearch = useCallback((value: string) => {
    setSearch(value);
  }, []);

  const options = useMemo(() => {
    const trimmed = search.trim().toLowerCase();
    if (trimmed) {
      return SEARCHABLE_CURRENCIES.filter(({ searchTokens }) =>
        searchTokens.includes(trimmed),
      ).map(toOption);
    }

    return [
      {
        label: t('SUGGESTED'),
        options: SUGGESTED_CURRENCIES.map(toOption),
      },
      {
        label: t('ALL_CURRENCIES'),
        options: OTHER_CURRENCIES.map(toOption),
      },
    ];
  }, [search, t]);

  return (
    <Select
      showSearch={searchShowSearch(onSearch)}
      notFoundContent={null}
      popupMatchSelectWidth={false}
      options={options}
      {...props}
    />
  );
};

export default CurrencySelect;
